import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ClientUserResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

interface ClientUsersState {
  // Map of clientID -> users array
  usersByClient: Record<string, ClientUserResponse[]>;
  loading: boolean;
  error: string | null;
  // Map of clientID -> lastFetched timestamp
  lastFetchedByClient: Record<string, number>;
  // Map of clientID -> cacheInfo
  cacheInfoByClient: Record<string, CacheInfo | null>;
  setUsers: (clientID: string, users: ClientUserResponse[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchUsers: (
    clientID: string,
    logout: () => void,
    accessToken: string,
    forceRefresh?: boolean
  ) => Promise<void>;
  getUsers: (clientID: string) => ClientUserResponse[];
  getCacheInfo: (clientID: string) => CacheInfo | null;
}

export const useClientUsersStore = create<ClientUsersState>()(devtools((set, get) => ({
  usersByClient: {},
  loading: false,
  error: null,
  lastFetchedByClient: {},
  cacheInfoByClient: {},
  setUsers: (clientID, users) => set((state) => ({
    usersByClient: { ...state.usersByClient, [clientID]: users },
    error: null,
  })),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  getUsers: (clientID) => get().usersByClient[clientID] || [],
  getCacheInfo: (clientID) => get().cacheInfoByClient[clientID] || null,
  fetchUsers: async (
    clientID: string,
    logout: () => void,
    accessToken: string,
    forceRefresh = false
  ) => {
    const state = get();

    // If we have cached data for this client and not forcing refresh, skip the fetch
    if (!forceRefresh && state.usersByClient[clientID]?.length > 0 && state.lastFetchedByClient[clientID]) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/clients/${clientID}/users`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      set((state) => ({
        usersByClient: { ...state.usersByClient, [clientID]: data.users },
        lastFetchedByClient: { ...state.lastFetchedByClient, [clientID]: Date.now() },
        cacheInfoByClient: { ...state.cacheInfoByClient, [clientID]: data.cacheInfo ?? null },
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'client-users-store' }));
